import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import type { TokenData } from '../types';

// Extended token data with chain information
export interface UnifiedToken extends TokenData {
  chain: 'ink' | 'ethereum' | 'monad';
  chainId: number;
  dexToolsUrl: string;
  explorerUrl: string;
  tradeUrl: string;
}

type ChainFilter = 'all' | 'ink' | 'ethereum' | 'monad';

/**
 * Hook to fetch tokens from all three chains (Ink, Ethereum, Monad)
 * with unified search and filtering capabilities
 */
export const useUnifiedTokens = (searchQuery: string = '', chainFilter: ChainFilter = 'all') => {
  const [tokens, setTokens] = useState<UnifiedToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const tokensPerPage = 20;

  // Chain configurations
  const chainConfigs = {
    ink: {
      table: 'tokens',
      chainId: 57073,
      name: 'Ink',
      dexToolsPrefix: 'https://www.dextools.io/app/en/ink/pair-explorer/',
      explorerPrefix: 'https://explorer.inkonchain.com/address/',
      logo: '/landing-assets/Ink.png'
    },
    ethereum: {
      table: 'tokens_eth',
      chainId: 1,
      name: 'Ethereum',
      dexToolsPrefix: 'https://www.dextools.io/app/en/ether/pair-explorer/',
      explorerPrefix: 'https://etherscan.io/address/',
      logo: '/landing-assets/ETH_logo.png'
    },
    monad: {
      table: 'tokens_monad',
      chainId: 143,
      name: 'Monad',
      dexToolsPrefix: 'https://www.dextools.io/app/en/monad/pair-explorer/',
      explorerPrefix: 'https://www.monadscan.com/address/',
      logo: '/monad-logo-nav-bar.png'
    }
  };

  const fetchTokens = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const allTokens: UnifiedToken[] = [];
      const searchTerm = searchQuery.toLowerCase().trim();

      // Determine which chains to fetch based on filter
      const chainsToFetch = chainFilter === 'all' 
        ? ['ink', 'ethereum', 'monad'] as const
        : [chainFilter] as const;

      // Fetch from each chain
      for (const chain of chainsToFetch) {
        const config = chainConfigs[chain];
        
        let query = supabase
          .from(config.table)
          .select('*');

        // Apply search filter if provided
        if (searchTerm) {
          query = query.or(
            `name.ilike.%${searchTerm}%,symbol.ilike.%${searchTerm}%,contract_address.ilike.%${searchTerm}%`
          );
        }

        // For Ink tokens, only get those on the ink network
        if (chain === 'ink') {
          query = query.eq('network', 'ink');
        }

        const { data, error: fetchError } = await query
          .order('created_at', { ascending: false })
          .limit(100);

        if (fetchError) {
          console.error(`Error fetching ${chain} tokens:`, fetchError);
          continue;
        }

        // Transform tokens to unified format
        const transformedTokens: UnifiedToken[] = (data || []).map(token => ({
          ...token,
          chain,
          chainId: config.chainId,
          dexToolsUrl: `${config.dexToolsPrefix}${token.pool_address || token.contract_address}`,
          explorerUrl: `${config.explorerPrefix}${token.contract_address}`,
          tradeUrl: chain === 'ink' 
            ? `https://relay.link/swap/ink?toToken=${token.contract_address}`
            : chain === 'ethereum'
            ? `https://app.uniswap.org/swap?chain=mainnet&inputCurrency=NATIVE&outputCurrency=${token.contract_address}`
            : `https://relay.link/swap/monad?toToken=${token.contract_address}`
        }));

        allTokens.push(...transformedTokens);
      }

      // Sort all tokens by created_at (most recent first)
      allTokens.sort((a, b) => {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });

      // Calculate pagination
      const totalCount = allTokens.length;
      const calculatedPages = Math.ceil(totalCount / tokensPerPage) || 1;
      
      // Apply pagination
      const startIdx = (page - 1) * tokensPerPage;
      const paginatedTokens = allTokens.slice(startIdx, startIdx + tokensPerPage);

      setTokens(paginatedTokens);
      setTotalPages(calculatedPages);
      setCurrentPage(page);

    } catch (err) {
      console.error('Error fetching unified tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tokens');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, chainFilter]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchTokens(page);
    }
  }, [fetchTokens, totalPages]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchTokens(1);
  }, [searchQuery, chainFilter]);

  return {
    tokens,
    loading,
    error,
    currentPage,
    totalPages,
    tokensPerPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    refetch: () => fetchTokens(currentPage),
    chainConfigs
  };
};

